/* Common Imports */

import React from "react";

/* Redux Imports */

import { serialize } from "next-mdx-remote/serialize";
import { connect } from "react-redux";
import StateInterface from "../../../redux-magic/state-interface";
import { updateReduxSessionThunk, wrapper } from "../../../redux-magic/store";
import {
	bottom_subscribe_buy_beegru_points,
	chat_states,
	notifications_page,
} from "../../../redux-magic/sub-state-data/sub-state-data";

/* Component Imports */

import loadable from "@loadable/component";
import { styled, Theme } from "@mui/material/styles";
import { useRouter } from "next/router";
import Layout from "../../../components/layout-components/Layout";

/* Loadable Components */

const Contact = loadable(() => import("../../../components/page-sections/ContactUs/Contact"));

/* Security library*/

import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Chip, Typography } from "@mui/material";
import { ObjectId } from "bson";
import { MDXRemote } from "next-mdx-remote";
import { useInView } from "react-intersection-observer";
import CardCarousel from "../../../components/page-sections/Index/CardCarousel";
import { validateClientSession } from "../../../lib/auth/helpers";
import authenticationTokenGenerator from "../../../lib/authentication_token_generator";
import handleDatabaseConnection from "../../../lib/handleDatabaseConnection";
import openViewTrackingWebsocket from "../../../lib/OpenViewTrackingWebsocket";
import ReadableDateString from "../../../lib/ReadableDateString";
import { FilesModel } from "../../../models";
import { BlogInterface } from "../../../models/blogs";

type SupportedLocales = "default" | "en-IN" | "kn-IN" | "ar-AE";

const BlogContentContainer = styled("div")(({ theme }) => ({
	padding: "5rem 1rem 1rem 1rem",
	[theme.breakpoints.up("md1190")]: {
		padding: "5rem 15rem 1rem 15rem",
	},
}));

const Title = styled(Typography)(({ theme }) => ({
	margin: "0.5rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: 500,
	fontSize: "2rem",
	lineHeight: "3rem",
	color: "#000000",
	letterSpacing: "0.25px",
	[theme.breakpoints.down("md1190")]: {
		fontSize: "1.5rem",
		lineHeight: "2.25rem",
	},
})) as typeof Typography;

const Description = styled(Typography)(({ theme }) => ({
	margin: "0.5rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1.5rem",
	lineHeight: "2rem",
	color: "#000000",
})) as typeof Typography;

const Author = styled(Typography)(({ theme }) => ({
	margin: "0.5rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1.25rem",
	lineHeight: "1.5rem",
	color: "#FC8019",
})) as typeof Typography;

const TimeToRead = styled(Typography)(({ theme }) => ({
	margin: "0.5rem 0rem 0.5rem 0rem",
	fontWeight: "500",
	fontSize: "1rem",
	lineHeight: "1.5rem",
	color: "#000000",
})) as typeof Typography;

const Tag = styled(Chip)(({ theme }) => ({
	// color: "#FC8019",
	fontSize: "1rem",
	fontWeight: "400",
	lineHeight: "1.5rem",
	borderRadius: "0.5rem",
	//borderColor: "#FC8019",
	padding: "0.25rem 0.5rem 0.25rem 0.5rem",
	"&:hover": {
		// background: "#FC8019",
		// color: "#FFFFFF",
		cursor: "pointer",
	},
	"&:focus": {
		// background: "#FC8019",
		// color: "#FFFFFF",
	},
	"&:active": {
		// background: "#FC8019",
		// color: "#FFFFFF",
	},
})) as typeof Chip;

const TagsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.5rem",
	width: "100%",
	padding: "0.5rem 0rem 0.5rem 0rem",
	margin: "0rem 0rem 0.5rem 0rem",
	// flexWrap: "wrap",
	overflowX: "scroll",
	"&::-webkit-scrollbar": {
		display: "none",
	},
})) as any;

const BlogCoverImage = styled("img")(({ theme }) => ({
	width: "100%",
	height: "auto",
	borderRadius: "1rem",
})) as any;

const AuthorCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.5rem",
})) as any;

const AuthorImage = styled("img")(({ theme }) => ({
	width: "3rem",
	height: "3rem",
	borderRadius: "0.5rem",
})) as any;

const AuthorNameAndPostIndicators = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	gap: "0.25rem",
})) as any;

const AuthorName = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: "400",
	lineHeight: "1rem",
	color: "#000000",
	cursor: "pointer",
	textDecoration: "none",
	"&:hover": {
		textDecoration: "underline",
	},
})) as typeof Typography;

const PostIndicators = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.25rem",
})) as any;

const PostIndicatorCreatedAt = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	fontWeight: "400",
	lineHeight: "0.75rem",
	color: "#666666",
})) as typeof Typography;

const PostIndicatorReadTime = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	fontWeight: "400",
	lineHeight: "0.75rem",
	color: "#FC8019",
})) as typeof Typography;

const AuthorAndShareBar = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
})) as any;

const ShareIconButtons = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	gap: "0.5rem",
})) as any;

const FAQQuestion = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: "500",
	lineHeight: "1rem",
	color: "#000000",
})) as typeof Typography;

const MDXWrapper = styled("div", {
	shouldForwardProp: (prop) => prop !== "type" && prop !== "withMargin",
})<any>(({ theme, type, withMargin }) => ({
	// display: type === "img" ? "inline-block" : "flex",
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	flexWrap: "wrap",
	gap: "0.5rem",
	width: "100%",
	maxWidth: "100%",
	// width: type === "img" ? "calc(50% - 0.5rem)" : "100%",
	// maxWidth: type === "img" ? "calc(50% - 0.5rem)" : "100%",
	// marginRight: type === "img" ? (withMargin ? "0.5rem" : "0rem") : "0rem",
	// [theme.breakpoints.down("md")]: {
	// 	width: "100%",
	// 	maxWidth: "100%",
	// 	marginRight: "0rem",
	// },
	"& img": {
		width: "100%",
		maxWidth: "100%",
		objectFit: "cover",
		objectPosition: "center",
		borderRadius: "1rem",
	},
})) as any;

const ContentContainer = styled("div")(({ theme }) => ({
	paddingTop: "0.5rem",
	paddingBottom: "0.5rem",
})) as any;

const FAQContainer = styled("div")(({ theme }) => ({
	paddingTop: "0.5rem",
	paddingBottom: "0.5rem",
})) as any;

const LinkedPropertiesOrServicesContainer = styled("div")(({ theme }) => ({
	paddingTop: "0.5rem",
	paddingBottom: "0.5rem",
	maxWidth: "100%",
})) as any;

// export const getStaticPaths = async () => {
// 	const paths: any = await (await fetch((process.env.PRODUCTION_API_URL || "") + "blog/paths")).json();
// 	return {
// 		paths: paths,
// 		fallback: false,
// 	};
// };

// export const getStaticProps = async (context: any) => {
// 	const blog_data: any = await (
// 		await fetch((process.env.PRODUCTION_API_URL || "") + "blog/" + context.params.author + "/" + context.params.slug)
// 	).json();
// 	return {
// 		props: {
// 			blog_data: blog_data,
// 		},
// 	};
// };

export const getServerSideProps = wrapper.getServerSideProps((store: any) => async (context: any) => {
	const server_session: any = await authenticationTokenGenerator(context);
	const blog_data: any = await (
		await fetch((process.env.PRODUCTION_API_URL || "") + "blog/" + context.params.author + "/" + context.params.slug)
	).json();
	await handleDatabaseConnection(true);
	const image_file_ui_names: Map<string, string> = new Map(
		(
			await Promise.all(
				blog_data.images.map((image_id: any) => {
					return FilesModel.findOne(
						{ _id: image_id },
						{
							_id: 1,
							"metadata.ui_name": 1,
						},
					).lean();
				}),
			)
		).map((db_response: any) => [db_response.metadata.ui_name, db_response._id]),
	);
	await handleDatabaseConnection(false);
	const faq_answers: any = await Promise.all(
		blog_data.faqs.map(({ question, answer }: { question: string; answer: Array<string> }) =>
			serialize(answer.join("\n")),
		),
	);
	return {
		props: {
			server_session: server_session,
			blog_data: {
				...blog_data,
				content: await Promise.all(
					blog_data.content.map((md: string) => {
						let current_md_line: string = md;
						let type: "p" | "img" = "p";
						if (current_md_line.startsWith("IMG![")) {
							type = "img";
							current_md_line = current_md_line.replace("IMG![", "![");
							const current_image_ui_name: string = current_md_line.split("(")[1].split(")")[0];
							const current_image_file_id: string =
								image_file_ui_names.get(current_image_ui_name) || new ObjectId().toString();
							current_md_line = current_md_line.replace(
								current_image_ui_name,
								(process.env.PRODUCTION_API_URL || "") + "blog/regular-image/" + current_image_file_id,
							);
						}
						return serialize(current_md_line, { scope: { type: type } });
					}),
				),
				faqs: blog_data.faqs.map((faq: any, index: number) => ({
					...faq,
					answer: faq_answers[index],
				})),
			},
		},
	};
});

const SingleBlogPage = ({
	server_session,
	session,
	redux_session,
	view_time_spent_id,
	dispatch,
	new_listing_url,
	new_business_profile_url,
	showInactivityDialog,
	theme,
	profile_context,
	notification_data,
	newNotification,
	search_filter_state,
	searchanimationsettings,
	bottom_buy_subscription,
	blog_data,
	login_flow_global_state,
	newChatMessage,
	realtimeWebsocket,
	chatStates,
}: {
	server_session: any;
	session: any;
	redux_session: StateInterface["redux_session"];
	view_time_spent_id: StateInterface["view_time_spent_id"];
	dispatch: Function;
	new_listing_url: StateInterface["new_listing_url"];
	new_business_profile_url: StateInterface["new_business_profile_url"];
	showInactivityDialog: boolean;
	theme: Theme;
	profile_context: StateInterface["profile_context"];
	notification_data: StateInterface["notifications_page"];
	newNotification?: StateInterface["notification"] | null;
	search_filter_state: StateInterface["search_filters_state"];
	searchanimationsettings: StateInterface["search_animation_settings"];
	bottom_buy_subscription: StateInterface["bottom_subscribe_buy_beegru_points"];
	login_flow_global_state: StateInterface["login_flow_global_state"];
	blog_data: BlogInterface;
	chatStates: StateInterface["chat_states"];
	newChatMessage?: StateInterface["chat_message"] | null;
	realtimeWebsocket: any;
}) => {
	const { ref: single_property_hot_properties_ref, inView: singlePropertyHotProperties_inView } = useInView({
		threshold: 0,
		triggerOnce: true,
	});
	/* Setting redux_session */

	const [reduxSessionUpdated, setReduxSessionUpdated] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (server_session && validateClientSession(server_session)) {
			dispatch(updateReduxSessionThunk(server_session));
		}
		setReduxSessionUpdated(true);
	}, [server_session, dispatch]);

	const router = React.useRef(useRouter()).current;

	const locale: SupportedLocales = router ? (router.locale ? (router.locale as SupportedLocales) : "en-IN") : "en-IN";

	/*

		& Let's declare a static bson object id to be used as a fallback in case the session is not available.

	*/

	const oid = new ObjectId().toHexString();

	/*

		& Let's declare the session id. We are using the session id to get the session id from the server session.

	*/

	const sessionId = server_session ? (server_session.session_id ? server_session.session_id : "") : "";

	/*

		& Let's start the view tracking process.
		& Let's declare the owner. We are using the owner to check if the user is the owner of the property.

	*/

	const creator_user_id: any = (blog_data.author.user_id as any)._id;

	const owner: boolean = redux_session
		? redux_session.user
			? redux_session.user.dbUser
				? redux_session.user.dbUser._id === creator_user_id
					? true
					: false
				: false
			: false
		: false;

	const [viewUpdated, setViewUpdated] = React.useState<boolean>(false);

	let ws = React.useRef<any>();

	React.useEffect(() => {
		if (reduxSessionUpdated && !owner && blog_data._id !== "") {
			/*

				& Let's track the view on this page by calling the openViewTrackingWebsocket() function.

			*/

			openViewTrackingWebsocket(
				dispatch,
				ws,
				view_time_spent_id,
				viewUpdated,
				setViewUpdated,
				creator_user_id,
				"blog",
				blog_data ? blog_data._id : oid,
				redux_session !== null ? true : false,
				redux_session
					? redux_session.user
						? redux_session.user.dbUser
							? redux_session.user.dbUser.profile_context
								? redux_session.user.dbUser.profile_context.is_business_profile
								: false
							: false
						: false
					: false,
				redux_session
					? redux_session.user
						? redux_session.user.dbUser
							? redux_session.user.dbUser._id
							: oid
						: oid
					: oid,
				redux_session
					? redux_session.user
						? redux_session.user.dbUser
							? redux_session.user.dbUser.profile_context
								? redux_session.user.dbUser.profile_context.business_profile_id
								: oid
							: oid
						: oid
					: oid,
				sessionId,
				redux_session ? redux_session.ip_address : "",
				redux_session ? redux_session.user_agent : "",
				router.pathname,
			);
		}

		/* eslint-disable-next-line react-hooks/exhaustive-deps*/
	}, [reduxSessionUpdated, view_time_spent_id, blog_data._id, owner]);

	/*

	* blog_data.linked_properties format doesnt match the expected format for the carousel

	*/

	const formattedDataForLinkedProperties = blog_data.linked_properties.map((id) => ({ _id: id }));
	const formattedDataForLinkedServices = blog_data.linked_services.map((id) => ({ _id: id }));
	const formattedDataForLinkedBlogs = blog_data.linked_blogs.map((id) => ({ _id: id }));

	const seo_script = {
		"@context": "https://schema.org",
		"@type": "BlogPosting",
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": `https://beegru.com/blog/${blog_data.author.first_name}-${blog_data.author.last_name}/${blog_data.slug}`,
		},
		headline: blog_data.title,
		image: blog_data.images.map((v) => (process.env.PRODUCTION_API_URL || "") + "blog/regular-image/" + v), // array of images
		author: {
			"@type": "Person",
			name: blog_data.author.first_name + " " + blog_data.author.last_name,
			url: `https://beegru.com/blog/${blog_data.author.first_name}-${blog_data.author.last_name}`,
		},
		datePublished: new Date(blog_data.created_at).toISOString(),
		publisher: {
			"@type": "Organization",
			name: "Beegru",
			logo: {
				"@type": "ImageObject",
				url: "https://cdn.beegru.com/images/icons/logo/og_512.jpg",
				width: 512,
				height: 512,
			},
		},
		url: `https://beegru.com/blog/${blog_data.author.first_name}-${blog_data.author.last_name}/${blog_data.slug}`,
		articleSection: "Real Estate",
		keywords: blog_data.meta_keywords,
		isAccessibleForFree: true,
	};

	return (
		<React.Fragment>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(seo_script),
				}}
			/>
			<Layout
				title={blog_data.meta_title}
				description={blog_data.meta_description}
				keywords={blog_data.meta_keywords.join(",")}
				canonicalUrl={process.env.PRODUCTION_URL + router.asPath.substring(1)}
				session={redux_session}
				redux_session_updated={reduxSessionUpdated}
				showInactivityDialog={showInactivityDialog}
				theme={theme}
				dispatch={dispatch}
				profile_context={profile_context}
				new_business_profile_url={new_business_profile_url ? new_business_profile_url : ""}
				new_listing_url={new_listing_url ? new_listing_url : ""}
				notifications={notification_data ? notification_data : notifications_page}
				new_notification={newNotification ? newNotification : null}
				search_filter_state={search_filter_state}
				searchanimationsettings={searchanimationsettings}
				bottom_buy_subscription={bottom_buy_subscription ? bottom_buy_subscription : bottom_subscribe_buy_beegru_points}
				realtime_websocket={realtimeWebsocket}
				chatStates={chatStates ? chatStates : chat_states}
				new_chat_message={newChatMessage ? newChatMessage : null}
				showChatFab
			>
				<BlogContentContainer>
					<BlogCoverImage
						src={(process.env.PRODUCTION_API_URL || "") + "blog/cover-image/" + blog_data.cover_image_file_id}
						alt={blog_data.cover_image_alt_text}
						loading="eager"
					/>
					<Title
						variant="h1"
						component="h1"
					>
						{blog_data.title}
					</Title>
					<TagsContainer>
						{blog_data.tags.map((tag: string, index: number) => (
							<Tag
								key={index}
								label={tag}
								variant="filled"
							/>
						))}
					</TagsContainer>
					<AuthorCard>
						<AuthorImage
							src={
								(process.env.PRODUCTION_API_URL || "") +
								"blog/persona-profile-picture/" +
								(blog_data.author.persona_id as any).profile_picture_file_id
							}
							alt={blog_data.author.first_name + " " + blog_data.author.last_name}
							loading="eager"
							onClick={(event: any) => {
								event.preventDefault();
								window.location.href =
									(process.env.PRODUCTION_URL || "") +
									"loading.html?callbackUrl=" +
									encodeURIComponent(
										"blog/" +
											blog_data.author.first_name.toLowerCase() +
											"-" +
											blog_data.author.last_name.toLowerCase(),
									);
							}}
						/>
						<AuthorAndShareBar>
							<AuthorNameAndPostIndicators>
								<AuthorName
									variant="body2"
									component="span"
									onClick={(event: any) => {
										event.preventDefault();
										window.location.href =
											(process.env.PRODUCTION_URL || "") +
											"loading.html?callbackUrl=" +
											encodeURIComponent(
												"blog/" +
													blog_data.author.first_name.toLowerCase() +
													"-" +
													blog_data.author.last_name.toLowerCase(),
											);
									}}
								>
									{blog_data.author.first_name + " " + blog_data.author.last_name}
								</AuthorName>
								<PostIndicators>
									<PostIndicatorCreatedAt
										variant="body2"
										component="span"
									>
										{ReadableDateString(new Date(blog_data.created_at), "half")}
									</PostIndicatorCreatedAt>
									&bull;
									<PostIndicatorReadTime
										variant="body2"
										component="span"
									>
										{blog_data.read_time_in_minutes + " min read"}
									</PostIndicatorReadTime>
								</PostIndicators>
							</AuthorNameAndPostIndicators>
							<ShareIconButtons></ShareIconButtons>
						</AuthorAndShareBar>
					</AuthorCard>
					<ContentContainer>
						{blog_data.content.map((md: any, index: number) => {
							return (
								<MDXWrapper
									key={index}
									type={md.scope.type}
									withMargin={md.scope.type === "img" && (blog_data.content[index + 1] as any).scope.type === "img"}
								>
									<MDXRemote {...md} />
								</MDXWrapper>
							);
						})}
					</ContentContainer>
					<FAQContainer>
						{blog_data.faqs.map((faq: any, index: number) => (
							<Accordion key={index}>
								<AccordionSummary expandIcon={<ExpandMore />}>
									<FAQQuestion
										variant="body1"
										component="h6"
									>
										{faq.question}
									</FAQQuestion>
								</AccordionSummary>
								<AccordionDetails>
									<MDXRemote {...faq.answer} />
								</AccordionDetails>
							</Accordion>
						))}
					</FAQContainer>
					<LinkedPropertiesOrServicesContainer>
						{formattedDataForLinkedProperties.length > 0 && (
							<CardCarousel
								data={formattedDataForLinkedProperties}
								loading={false}
								type="custom_properties"
								customTitle={blog_data.linked_properties_title}
								profile_context={profile_context}
								dispatch={dispatch}
								session={redux_session}
								innerRef={single_property_hot_properties_ref}
								login_flow_global_state={login_flow_global_state}
							/>
						)}
					</LinkedPropertiesOrServicesContainer>
					<LinkedPropertiesOrServicesContainer>
						{formattedDataForLinkedServices.length > 0 && (
							<CardCarousel
								data={formattedDataForLinkedServices}
								loading={false}
								type="custom_services"
								customTitle={blog_data.linked_services_title}
								profile_context={profile_context}
								dispatch={dispatch}
								session={redux_session}
								innerRef={single_property_hot_properties_ref}
								login_flow_global_state={login_flow_global_state}
							/>
						)}
					</LinkedPropertiesOrServicesContainer>
					<LinkedPropertiesOrServicesContainer>
						{formattedDataForLinkedBlogs.length > 0 && (
							<CardCarousel
								data={formattedDataForLinkedBlogs}
								loading={false}
								type="blogs"
								customTitle={"Featured blogs"}
								profile_context={profile_context}
								dispatch={dispatch}
								session={redux_session}
								innerRef={single_property_hot_properties_ref}
								login_flow_global_state={login_flow_global_state}
							/>
						)}
					</LinkedPropertiesOrServicesContainer>
				</BlogContentContainer>
			</Layout>
		</React.Fragment>
	);
};

export default connect(
	({
		globalState: {
			profile_context,
			new_listing_url,
			new_business_profile_url,
			notifications_page,
			redux_session,
			view_time_spent_id,
			search_filters_state,
			search_animation_settings,
			bottom_subscribe_buy_beegru_points,
			login_flow_global_state,
			chat_states,
		},
	}: {
		globalState: {
			profile_context: StateInterface["profile_context"];
			new_listing_url: StateInterface["new_listing_url"];
			new_business_profile_url: StateInterface["new_business_profile_url"];
			notifications_page: StateInterface["notifications_page"];
			redux_session: StateInterface["redux_session"];
			view_time_spent_id: StateInterface["view_time_spent_id"];
			search_filters_state: StateInterface["search_filters_state"];
			search_animation_settings: StateInterface["search_animation_settings"];
			bottom_subscribe_buy_beegru_points: StateInterface["bottom_subscribe_buy_beegru_points"];
			login_flow_global_state: StateInterface["login_flow_global_state"];
			chat_states: StateInterface["chat_states"];
		};
	}) => ({
		profile_context: profile_context,
		new_listing_url: new_listing_url,
		new_business_profile_url: new_business_profile_url,
		notification_data: notifications_page,
		redux_session: redux_session,
		view_time_spent_id: view_time_spent_id,
		search_filter_state: search_filters_state,
		searchanimationsettings: search_animation_settings,
		bottom_buy_subscription: bottom_subscribe_buy_beegru_points,
		login_flow_global_state: login_flow_global_state,
		chatStates: chat_states,
	}),
)(SingleBlogPage);
